import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingGuide = () => {
  const [selectedPlan, setSelectedPlan] = useState('growth');

  const pricingPlans = [
    {
      id: 'starter',
      name: 'MVP Development',
      description: 'Perfect for startups validating their SaaS concept with a minimum viable product',
      price: '$25,000',
      duration: '8-12 weeks',
      icon: 'Rocket',
      features: [
        'Core feature development',
        'Responsive web application',
        'Basic API integration',
        'User authentication',
        'Admin dashboard',
        'Cloud deployment',
        '3 months support',
        'Technical documentation'
      ],
      ideal: 'Early-stage startups',
      popular: false
    },
    {
      id: 'growth',
      name: 'Full Product Development',
      description: 'Comprehensive solution for companies ready to scale their SaaS platform',
      price: '$75,000',
      duration: '16-24 weeks',
      icon: 'TrendingUp',
      features: [
        'Everything in MVP',
        'Advanced feature set',
        'Mobile applications (iOS/Android)',
        'Third-party integrations',
        'Payment processing',
        'Analytics & reporting',
        'Performance optimization',
        '6 months support',
        'Team training',
        'Marketing website'
      ],
      ideal: 'Growing companies',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise Solution',
      description: 'Custom enterprise-grade platform with advanced security and scalability',
      price: 'Custom',
      duration: '24+ weeks',
      icon: 'Building2',
      features: [
        'Everything in Growth',
        'Custom architecture design',
        'Advanced security features',
        'Multi-tenant infrastructure',
        'Custom integrations',
        'Dedicated team',
        'Priority support',
        'SLA guarantees',
        'Compliance certification',
        'White-label options',
        'Ongoing maintenance'
      ],
      ideal: 'Enterprise organizations',
      popular: false
    }
  ];

  const additionalServices = [
    { name: 'UI/UX Design', price: '$5,000 - $15,000', icon: 'Palette' },
    { name: 'Mobile App Development', price: '$20,000 - $50,000', icon: 'Smartphone' },
    { name: 'API Development', price: '$10,000 - $30,000', icon: 'Code2' },
    { name: 'DevOps & Infrastructure', price: '$5,000 - $20,000', icon: 'Server' },
    { name: 'Quality Assurance', price: '$8,000 - $25,000', icon: 'CheckCircle2' },
    { name: 'Ongoing Maintenance', price: '$2,000 - $10,000/month', icon: 'Settings' }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-muted/30" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4 md:mb-6">
            <Icon name="DollarSign" size={18} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-medium font-cta text-primary">
              Transparent Pricing
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4 md:mb-6">
            Investment Guide for Your SaaS Journey
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Clear, transparent pricing with no hidden costs. Choose the engagement model that aligns with your business goals and budget.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {pricingPlans?.map((plan) => (
            <div
              key={plan?.id}
              className={`relative bg-card border-2 rounded-lg p-6 md:p-8 transition-all duration-300 ${
                plan?.popular
                  ? 'border-primary shadow-xl scale-105'
                  : 'border-border hover:border-primary/50 hover:shadow-lg'
              }`}
            >
              {plan?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs md:text-sm font-medium font-cta shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6 md:mb-8">
                <div className={`w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 md:mb-6 rounded-lg flex items-center justify-center ${
                  plan?.popular ? 'bg-primary' : 'bg-primary/10'
                }`}>
                  <Icon
                    name={plan?.icon}
                    size={32}
                    color={plan?.popular ? 'var(--color-primary-foreground)' : 'var(--color-primary)'}
                  />
                </div>
                <h3 className="text-xl md:text-2xl font-bold font-headline text-foreground mb-2">
                  {plan?.name}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground font-body mb-4">
                  {plan?.description}
                </p>
                {/* <div className="mb-2">
                  <span className="text-3xl md:text-4xl font-bold font-headline text-primary">
                    {plan?.price}
                  </span>
                </div> */}
                <div className="flex items-center justify-center space-x-2 text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span className="text-xs md:text-sm font-body">{plan?.duration}</span>
                </div>
              </div>

              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {plan?.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <Icon name="CheckCircle2" size={18} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm md:text-base text-foreground font-body">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-border mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground font-body">Ideal for:</span>
                  <span className="font-medium text-foreground font-body">{plan?.ideal}</span>
                </div>
              </div>

              <Button
                variant={plan?.popular ? 'default' : 'outline'}
                size="default"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => window.location.href = '/contact'}
              >
                Get Started
              </Button>
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-lg p-6 md:p-8 lg:p-10">
          <div className="text-center mb-8 md:mb-10">
            <h3 className="text-2xl md:text-3xl font-bold font-headline text-foreground mb-3 md:mb-4">
              Additional Services & Add-ons
            </h3>
            <p className="text-sm md:text-base text-muted-foreground font-body max-w-2xl mx-auto">
              Enhance your project with specialized services tailored to your specific requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {additionalServices?.map((service, index) => (
              <div
                key={index}
                className="bg-muted/50 rounded-lg p-4 md:p-6 hover:bg-muted transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={service?.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <h4 className="text-base md:text-lg font-semibold font-headline text-foreground ">
                      {service?.name}
                    </h4>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 md:mt-16 bg-primary/5 border border-primary/20 rounded-lg p-6 md:p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex-1">
              <h3 className="text-xl md:text-2xl font-bold font-headline text-foreground mb-2 md:mb-3">
                Need a Custom Quote?
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-body">
                Every project is unique. Schedule a free consultation to discuss your specific requirements and receive a detailed proposal tailored to your needs.
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => window.location.href = '/contact'}
              className="whitespace-nowrap"
            >
              Schedule Free Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingGuide;